import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState, Pagination } from '@/components/ui/data-state'
import { PAGE_LIMIT_MAX } from '@/lib/api/common'
import { inventoryRecipePath } from '@/routes/paths'

import { useRecipeList, useStockItems, useStockItemsById, useUnitCatalog } from '../hooks'
import type { StockItem } from '../types'
import { RecipesTable } from './recipes-table'

const PAGE_SIZE = 25

/** One page of the stock list, the editor's own — so the two share a cache entry. */
const STOCK_PAGE_QUERY = { limit: PAGE_LIMIT_MAX, offset: 0 }

interface RecipesPanelProps {
  /**
   * Picks out one item's recipe — set when the merchant arrives from that item's
   * Recipe button on the Stock Items tab. `null` lists every recipe.
   */
  stockItemId: string | null
  /** Drops the item, back to every recipe. */
  onShowAll: () => void
}

/**
 * Every recipe, newest first — or one item's, when the merchant came from it.
 *
 * Clicking a row opens its recipe on a page of its own, ready for its
 * ingredients. There is no "new recipe" here: every product made from other
 * items comes with a draft recipe, which is listed here waiting to be filled in.
 * The recipe page is the only place a recipe is edited: the Stock Items tab
 * links here rather than opening a form of its own.
 */
export function RecipesPanel({ stockItemId, onShowAll }: RecipesPanelProps) {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  /** The item the page was last at, so a change of item starts it from the top. */
  const [pagedFor, setPagedFor] = useState(stockItemId)

  // An item's recipe is one row on one page; the page the full list was on would
  // point past it. Reset while rendering, the way React suggests for state that
  // follows a prop, so the stale offset is never fetched.
  if (pagedFor !== stockItemId) {
    setPagedFor(stockItemId)
    setPage(0)
  }

  const { recipes, total, isLoading, isRefreshing, error, retry } = useRecipeList(
    page,
    PAGE_SIZE,
    stockItemId,
  )

  const itemIds = useMemo(
    () => [
      ...new Set([
        ...recipes.map((recipe) => recipe.stockItemId),
        ...(stockItemId ? [stockItemId] : []),
      ]),
    ],
    [recipes, stockItemId],
  )
  const items = useStockItemsById(itemIds)

  /* The ingredients, for each row's cost. Most come from one page of the stock
     list — the same page the recipe editor offers, so it is usually cached
     already — and only the ones past it are read one by one. */
  const { items: stockPage } = useStockItems(STOCK_PAGE_QUERY)
  const { catalog: units } = useUnitCatalog()
  const missingIngredientIds = useMemo(() => {
    const onPage = new Set(stockPage.map((stockItem) => stockItem.id))
    return [
      ...new Set(recipes.flatMap((recipe) => recipe.lines.map((line) => line.itemId))),
    ].filter((id) => !onPage.has(id))
  }, [recipes, stockPage])
  const missingIngredients = useStockItemsById(missingIngredientIds)
  const ingredients = useMemo(() => {
    const byId = new Map<string, StockItem>(missingIngredients)
    for (const stockItem of stockPage) byId.set(stockItem.id, stockItem)
    return byId
  }, [stockPage, missingIngredients])

  const pickedItem = stockItemId ? items.get(stockItemId) : undefined

  return (
    <>
      <ModuleIntro description="What goes into each product you make. Click a recipe to add its ingredients." />

      {stockItemId && (
        <div className="border-border bg-surface flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3 text-sm">
          <span className="flex-1">
            Showing the recipe for{' '}
            <span className="font-medium">{pickedItem?.name ?? 'this item'}</span>.
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="border-border h-9 rounded-full border px-4"
            onPress={onShowAll}
          >
            Show all recipes
          </Button>
        </div>
      )}

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="your recipes" error={error} onRetry={retry} />
      ) : recipes.length === 0 ? (
        stockItemId ? (
          <EmptyState
            title="This item has no recipe."
            description="A recipe comes with an item when it is created as “Made from other items”."
            action={{ label: 'Show all recipes', onPress: onShowAll }}
          />
        ) : (
          <EmptyState
            title="No recipes yet."
            description="Each product made from other items gets a recipe of its own, listed here for you to add its ingredients."
          />
        )
      ) : (
        <>
          <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
            <RecipesTable
              recipes={recipes}
              items={items}
              ingredients={ingredients}
              units={units}
              onOpen={(recipe) => navigate(inventoryRecipePath(recipe.id))}
            />
          </div>

          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}
    </>
  )
}
